/**
 * useSelfHealCustomNow — operator-triggered, immediate custom-source heal
 * ("Self-Heal Now") for a CUSTOM `file_missing` print-file issue.
 *
 * Calls `trpc.printAgentAdmin.selfHealCustomNow`, which runs the full
 * fetch-source → platen-resize → upload → verify → resolve pipeline
 * synchronously server-side, BYPASSING the bridge queue's poison cap — the
 * manual retry for issues the bridge gave up on. Outcomes:
 *   - `healed`       → file written + verified on the bucket the ERP probes;
 *                      issue resolved. There is no waiting outcome: the write
 *                      target IS the existence target, so a verified write is
 *                      healed (BLU-826).
 *   - `requeued`     → `.arx4`/DTG location: server can't RIP; the bridge
 *                      queue was un-poisoned and will retry next poll.
 *   - `healing`      → BLU-1309: still running after 20 s; the person who
 *                      started it gets a notification when it ends (in their
 *                      profile language, read server-side).
 *   - `failed`       → with a reason (`no_source_url` → upload manually).
 */

import { useCallback, useState } from 'react';
import { trpc } from '@/lib/trpc';

export type SelfHealNowStatus = 'healed' | 'requeued' | 'healing' | 'failed';

export interface SelfHealNowResult {
  status: SelfHealNowStatus;
  reason?: string;
}

interface UseSelfHealCustomNowResult {
  /** Run the heal for one issue. Throws on auth/validation errors (caller
   *  surfaces via getUserMessage); outcome failures come back as `failed`. */
  selfHealNow: (issueId: string) => Promise<SelfHealNowResult>;
  /** Issue id currently healing (single-flight per row), or null. */
  healingIssueId: string | null;
}

export function useSelfHealCustomNow(): UseSelfHealCustomNowResult {
  const mutation = trpc.printAgentAdmin.selfHealCustomNow.useMutation();
  const [healingIssueId, setHealingIssueId] = useState<string | null>(null);

  const selfHealNow = useCallback(
    async (issueId: string): Promise<SelfHealNowResult> => {
      setHealingIssueId(issueId);
      try {
        const result = await mutation.mutateAsync({ issueId });
        return result as SelfHealNowResult;
      } finally {
        setHealingIssueId(null);
      }
    },
    [mutation]
  );

  return { selfHealNow, healingIssueId };
}
